// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Merging imported OpenStreetMap holes onto the scorecard the user already has.
//
// The rule is that an import fills gaps and never clears. OSM coverage is patchy — a course
// can come back with unreadable stroke indexes, or with nothing at all for some holes — and
// blanking what is already on the scorecard would turn "import" into "lose the data you
// typed", which is not a trade anyone agreed to.

/** A hole row as the course-config form holds it: every field a string, blank when unset. */
export type HoleRowValues = {
  number: number;
  par: string;
  strokeIndex: string;
  lengthMeters: string;
};

/** A hole as OpenStreetMap supplied it. Null means OSM had nothing readable for that field. */
export type ImportedHole = {
  number: number;
  par: number | null;
  strokeIndex: number | null;
  lengthMeters: number | null;
};

const keepUnlessSupplied = (incoming: number | null | undefined, existing: string): string =>
  incoming != null ? String(incoming) : existing;

/**
 * Applies imported holes to the existing rows.
 *
 * A hole OSM does not cover is returned untouched, and a field OSM has no value for keeps
 * whatever was there.
 */
export const mergeImportedHoles = <T extends HoleRowValues>(
  rows: readonly T[],
  imported: readonly ImportedHole[]
): T[] =>
  rows.map((row) => {
    const match = imported.find((hole) => hole.number === row.number);
    if (!match) return row;
    return {
      ...row,
      par: keepUnlessSupplied(match.par, row.par),
      strokeIndex: keepUnlessSupplied(match.strokeIndex, row.strokeIndex),
      lengthMeters: keepUnlessSupplied(match.lengthMeters, row.lengthMeters),
    };
  });
